/* xPT Notification */
export interface XptNotification {
  notification_id: string;
  source: string; // producer id, e.g. "access_management"
  event: string; // producer event/verb, e.g. "submit" | "approve" | ...
  payload: string; // AWSJSON (JSON-serialized string)
  is_persistent: boolean;
  is_read: boolean;
}

/** The codegen'd subscription payload: every non-key field is nullable, and the publish path never
 * sends is_read (PublishNotificationInput has no such field), so it always arrives absent. */
interface PushedNotification {
  notification_id: string;
  source: string;
  event: string;
  payload?: string | null;
  is_persistent?: boolean | null;
  is_read?: boolean | null;
}

/** Normalize a pushed (subscription) notification into the domain shape, coalescing the nullable
 * fields: no payload -> "{}", missing flags -> false. A pushed item is unread (is_read=false), which
 * is correct — it was just published. */
export const fromPushedNotification = (n: PushedNotification): XptNotification => ({
  notification_id: n.notification_id,
  source: n.source,
  event: n.event,
  payload: n.payload ?? '{}',
  is_persistent: n.is_persistent ?? false,
  is_read: n.is_read ?? false
});

/* Access-management notification payload */

export const ACCESS_MANAGEMENT_SOURCE = 'access_management';

export type AccessRequestKind = 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface AccessRequestBase {
  request_id: string;
  bg_id: number;
  business_group_name: string;
  requested_role: string;
  cost_centers: string[];
  requester_alias: string;
  requester_name: string;
  beneficiary_alias: string;
  beneficiary_name: string;
}

export type ResolutionAction = 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface ApprovalOutcome {
  is_partial_approval?: boolean;
  requested_cost_centers?: string[];
  granted_cost_centers?: string[];
}

export interface SubmittedAccessRequest extends AccessRequestBase, ApprovalOutcome {
  kind: 'SUBMITTED';
  resolution_action?: ResolutionAction;
  resolved_by?: string;
  resolved_by_name?: string;
}

/** The requester/beneficiary's standalone outcome item (own type: APPROVED / REJECTED). */
export interface ResolvedAccessRequest extends AccessRequestBase, ApprovalOutcome {
  kind: 'APPROVED' | 'REJECTED' | 'CANCELLED';
  resolved_by?: string;
  resolved_by_name?: string;
}

export type AccessRequestNotification = SubmittedAccessRequest | ResolvedAccessRequest;

const KIND_BY_EVENT: Record<string, AccessRequestKind> = {
  submit: 'SUBMITTED',
  approve: 'APPROVED',
  reject: 'REJECTED',
  cancel: 'CANCELLED'
};

export const parseAccessRequestNotification = (notification: XptNotification): AccessRequestNotification | null => {
  if (notification.source !== ACCESS_MANAGEMENT_SOURCE) return null;
  const kind = KIND_BY_EVENT[notification.event];
  if (!kind) return null;
  try {
    const payload = JSON.parse(notification.payload ?? '');
    // Guard against a malformed payload (e.g. a double-encoded string that parses to a string, or a
    // non-object). Returning null here makes the presenter fall back to the generic message instead
    // of rendering "undefined (undefined) requested undefined for undefined".
    if (typeof payload !== 'object' || payload === null || !payload.request_id) {
      console.error('parseAccessRequestNotification: unexpected payload shape', {
        notification_id: notification.notification_id,
        event: notification.event,
        payload: notification.payload
      });
      return null;
    }
    return { ...payload, kind } as AccessRequestNotification;
  } catch (e) {
    console.error('parseAccessRequestNotification: payload is not valid JSON', {
      notification_id: notification.notification_id,
      payload: notification.payload,
      error: e
    });
    return null;
  }
};

/**
 * Whether an access-request notification has reached an outcome.
 *
 * True in both shapes a resolution arrives in: the requester's own APPROVED/REJECTED/CANCELLED
 * item, and the reviewer's SUBMITTED item reconciled in place with a `resolution_action`.
 * Anything that is not a recognisable access request is treated as unresolved.
 */
const isResolved = (notification: XptNotification): boolean => {
  const request = parseAccessRequestNotification(notification);
  if (!request) {
    return false;
  }
  return request.kind !== 'SUBMITTED' || Boolean(request.resolution_action);
};

/**
 * Whether the notification still represents an action waiting on someone.
 *
 * `is_persistent` marks an item as "an action pending until resolved", but the flag is set by the
 * publisher and a resolved item is reconciled in place under its original notification_id — so a
 * reviewer's item can come back resolved while still carrying is_persistent. Reading the payload
 * as well means a resolved item stops behaving like an outstanding one even if the flag lingers,
 * instead of being stuck permanently unread and undismissable in the bell count.
 */
export const isActionOutstanding = (notification: XptNotification): boolean =>
  notification.is_persistent && !isResolved(notification);

/** Whether a notification counts as unread. An item representing an outstanding action stays
 * unread regardless of the read watermark; otherwise it's unread until read.
 * Single source of truth for the bell badge count and the panel's unread styling. */
export const isUnread = (n: XptNotification): boolean => !n.is_read || isActionOutstanding(n);

/** Whether the user may dismiss a notification. An outstanding action cannot be hidden, since
 * doing so would lose the task — resolved items are informational and can be dismissed. */
export const isDismissible = (n: XptNotification): boolean => !isActionOutstanding(n);

/**
 * Orders notifications so the ones still asking something of the user come first.
 *
 * The server returns newest-first, which buries a request awaiting review as soon as a few
 * informational outcomes arrive after it — and with a paged list it can fall off the first page
 * entirely. Sorting is stable, so the server's ordering is preserved within each group: newest
 * first among the outstanding items, then newest first among the resolved ones.
 *
 * Returns a new array; the input is not mutated.
 */
export const prioritizeActionable = (items: XptNotification[]): XptNotification[] =>
  [...items].sort((a, b) => Number(isActionOutstanding(b)) - Number(isActionOutstanding(a)));

/**
 * Whether a notification should never reach the user.
 *
 * A cancellation is the requester withdrawing their own request: they already know they did
 * it, and for a reviewer there is no longer anything to review. Announcing it only adds noise
 * to the bell, so cancelled requests are dropped rather than shown as a resolved outcome.
 *
 * Covers both shapes a cancellation arrives in: the requester/beneficiary's standalone
 * CANCELLED item, and the reviewer's SUBMITTED item reconciled in place with
 * `resolution_action` of CANCELLED (its `kind` stays SUBMITTED).
 *
 * Anything this function cannot parse is left visible — a broken payload still deserves the
 * "could not be loaded" row rather than silently disappearing.
 */
export const isSuppressed = (notification: XptNotification): boolean => {
  const request = parseAccessRequestNotification(notification);
  if (!request) {
    return false;
  }
  if (request.kind === 'CANCELLED') {
    return true;
  }
  return request.kind === 'SUBMITTED' && request.resolution_action === 'CANCELLED';
};
